import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { JLPTLevel, Tag, Video, VideoCategory } from "@/types/video";
import {
  categoryBelongsToTab,
  getCategoryLabel,
  normalizeCategoryList,
} from "@/lib/category-config";

interface DbVideoRow {
  id: string;
  title: string;
  description: string | null;
  video_url: string;
  thumbnail_url: string | null;
  duration: number | null;
  jlpt_level: string;
  category: string | null;
  categories?: string[] | null;
  series_id?: string | null;
  is_premium: boolean | null;
  is_published: boolean | null;
  view_count: number | null;
  like_count: number | null;
  created_at: string;
  series_order?: number | null;
}

interface DbAudioRow {
  id: string;
  title: string;
  description: string | null;
  audio_url: string;
  thumbnail_url: string | null;
  duration: number | null;
  jlpt_level: string | null;
  category: string | null;
  categories?: string[] | null;
  series_id?: string | null;
  series_order?: number | null;
  view_count: number | null;
  like_count: number | null;
  created_at: string;
}

function formatDuration(seconds: number | null): string {
  if (!seconds || seconds <= 0) return "00:00";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

function inferTopLevelCategory(categories: string[]): VideoCategory {
  if (categories.some((cat) => categoryBelongsToTab(cat, "textbook"))) return "textbook";
  if (categories.some((cat) => categoryBelongsToTab(cat, "news"))) return "news";
  if (categories.some((cat) => categoryBelongsToTab(cat, "podcast"))) return "podcast";
  return "all";
}

function buildTag(categoryValue: string): Tag {
  const colorMap: Record<string, Tag["color"]> = {
    textbook: "green",
    learning: "green",
    news: "orange",
    documentary: "orange",
    podcast: "purple",
    conversation: "blue",
    anime: "purple",
    drama: "purple",
    music: "blue",
    story: "blue",
    lifestyle: "purple",
  };

  return {
    label: getCategoryLabel(categoryValue),
    color: colorMap[categoryValue] || "blue",
  };
}

function mapDbVideoToVideo(row: DbVideoRow): Video {
  const categories = normalizeCategoryList([
    row.category,
    ...(Array.isArray(row.categories) ? row.categories : []),
  ]);

  const topLevelCategory = inferTopLevelCategory(categories);
  const tags: Tag[] = categories.slice(0, 3).map(buildTag);

  return {
    id: row.id,
    episodeNumber: Number(row.series_order || 1),
    title: row.title,
    thumbnailUrl: row.thumbnail_url || row.video_url,
    videoUrl: row.video_url,
    contentType: "video",
    authorName: "dango",
    duration: formatDuration(row.duration),
    durationSeconds: Number(row.duration || 0),
    rating: 4.5,
    tags,
    jlptLevel: (row.jlpt_level || "N5") as JLPTLevel,
    category: topLevelCategory,
    categories,
    dbCategory: categories[0] || undefined,
    series: row.series_id || undefined,
    viewCount: row.view_count || 0,
    likeCount: row.like_count || 0,
    description: row.description || undefined,
    publishedAt: row.created_at,
    isLocked: false,
  };
}

function mapDbAudioToVideo(row: DbAudioRow): Video {
  const categories = normalizeCategoryList([
    row.category,
    ...(Array.isArray(row.categories) ? row.categories : []),
  ]);
  const topLevelCategory = inferTopLevelCategory(categories);
  const tags: Tag[] = categories.slice(0, 3).map(buildTag);

  return {
    id: row.id,
    episodeNumber: Number(row.series_order || 1),
    title: row.title,
    thumbnailUrl: row.thumbnail_url || "",
    videoUrl: row.audio_url,
    contentType: "audio",
    authorName: "dango",
    duration: formatDuration(row.duration),
    durationSeconds: Number(row.duration || 0),
    rating: 4.5,
    tags,
    jlptLevel: (row.jlpt_level || "N5") as JLPTLevel,
    category: topLevelCategory,
    categories,
    dbCategory: categories[0] || undefined,
    series: row.series_id || undefined,
    viewCount: row.view_count || 0,
    likeCount: row.like_count || 0,
    description: row.description || undefined,
    publishedAt: row.created_at,
    isLocked: false,
  };
}

export function useVideos() {
  return useQuery({
    queryKey: ["videos", "published"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("videos")
        .select("*")
        .eq("is_published", true)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching videos", error);
        throw error;
      }

      return ((data || []) as DbVideoRow[]).map(mapDbVideoToVideo);
    },
  });
}

export function useVideo(videoId: string) {
  return useQuery({
    queryKey: ["video", videoId],
    enabled: !!videoId,
    queryFn: async () => {
      const { data: videoData, error: videoError } = await supabase
        .from("videos")
        .select("*")
        .eq("id", videoId)
        .maybeSingle();

      if (!videoError && videoData) {
        return mapDbVideoToVideo(videoData as DbVideoRow);
      }

      const { data: audioData, error: audioError } = await supabase
        .from("audios")
        .select("*")
        .eq("id", videoId)
        .maybeSingle();

      if (audioError) {
        console.error("Error fetching content", videoError || audioError);
        throw videoError || audioError;
      }

      if (!audioData) {
        throw new Error("Content not found");
      }

      return mapDbAudioToVideo(audioData as DbAudioRow);
    },
  });
}

export function usePublishedVideoCount() {
  return useQuery({
    queryKey: ["videos", "published", "count"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("videos")
        .select("id", { count: "exact", head: true })
        .eq("is_published", true);

      if (error) {
        console.error("Error fetching published video count", error);
        throw error;
      }

      return count || 0;
    },
  });
}

export function usePlayableVideoCountBySeries(seriesIds: string[]) {
  return useQuery({
    queryKey: ["videos", "published", "count", "series", seriesIds],
    enabled: Array.isArray(seriesIds) && seriesIds.length > 0,
    queryFn: async () => {
      const { count, error } = await supabase
        .from("videos")
        .select("id", { count: "exact", head: true })
        .eq("is_published", true)
        .in("series_id", seriesIds);

      if (error) {
        console.error("Error fetching playable video count", error);
        throw error;
      }

      return count || 0;
    },
  });
}
